<script setup lang="ts">
import { computed, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    modelValue?: number | '';
    inputId?: string;
    min?: number;
    max?: number;
    showButtons?: boolean;
    class?: string;
    step?: number;
    invalid?: boolean;
    disabled?: boolean;
    mode?: 'decimal' | 'currency' | 'standard';
    minFractionDigits?: number;
  }>(),
  {
    placeholder: '',
    inputId: '',
    min: undefined,
    max: undefined,
    class: '',
    step: 1,
    modelValue: 0,
    showButtons: false,
    invalid: false,
    disabled: false,
    mode: 'standard',
    minFractionDigits: undefined,
  },
);

// const internalValue = ref<number | string>(props.modelValue === '' ? 0 : props.modelValue)

const emit = defineEmits(['update:modelValue', 'blur']);

const formatValue = (value: number | string): string => {
  if (value === '' || value === 0) {
    if (props.minFractionDigits !== undefined && props.minFractionDigits > 0) {
      return (0).toFixed(props.minFractionDigits);
    }
    return value === '' ? '' : '0';
  }
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '0';
  if (props.minFractionDigits !== undefined) {
    return numValue.toFixed(props.minFractionDigits);
  }
  return String(numValue);
};

const internalValue = ref<number | string>(props.modelValue === '' ? 0 : props.modelValue);

// Track if user is actively typing (to prevent watcher from overwriting intermediate states like "4.")
const isUserTyping = ref(false);

watch(
  () => props.modelValue,
  (newValue) => {
    // Don't overwrite while user is typing - this prevents "4." from being reset to "4"
    if (isUserTyping.value) return;

    internalValue.value = newValue === '' ? 0 : newValue;
  },
);

const onBlur = () => {
  // User stopped typing
  isUserTyping.value = false;

  // Format the display value on blur if minFractionDigits is set
  if (props.minFractionDigits !== undefined) {
    const numValue = typeof internalValue.value === 'string' ? parseFloat(internalValue.value) : internalValue.value;
    if (!isNaN(numValue)) {
      internalValue.value = formatValue(numValue);
    }
  }
  emit('blur');
};

const updateValue = () => {
  // Mark that user is actively typing
  isUserTyping.value = true;

  // If empty string, emit 0
  if (internalValue.value === '') {
    emit('update:modelValue', 0);
    return;
  }

  // If user input is a string, do partial cleanup
  if (typeof internalValue.value === 'string') {
    let raw = internalValue.value;

    // 1) Replace commas with periods
    raw = raw.replace(/,/g, '.');

    // 2) Remove any invalid characters (anything not 0-9 or ".")
    raw = raw.replace(/[^0-9.]/g, '');

    // 3) If the first character is ".", prepend "0"
    if (raw.startsWith('.')) {
      raw = '0' + raw;
    }

    // 4) Allow only ONE decimal point
    const firstDotIndex = raw.indexOf('.');
    if (firstDotIndex !== -1) {
      // If there's a second dot, cut off everything after it
      const secondDotIndex = raw.indexOf('.', firstDotIndex + 1);
      if (secondDotIndex !== -1) {
        raw = raw.substring(0, secondDotIndex);
      }
    }

    // Update the internal (displayed) value with the partially cleaned string
    internalValue.value = raw;

    // Meanwhile, parse into a number for the outside world
    let numericValue = parseFloat(raw);
    if (isNaN(numericValue)) numericValue = 0;

    // Emit the numeric version to the parent
    emit('update:modelValue', numericValue);
  }
  // If it's already a number (e.g. from increment/decrement)
  else {
    emit('update:modelValue', internalValue.value);
  }
};

// watch(
//   () => props.modelValue,
//   (newValue) => {
//     internalValue.value = newValue === '' ? 0 : newValue
//   }
// )

const increment = () => {
  if (internalValue.value === '') {
    internalValue.value = 1;
  } else {
    // Convert string to number if needed
    const currentValue = typeof internalValue.value === 'string' ? parseFloat(internalValue.value.replace(',', '.')) : internalValue.value;

    // Check if it's a valid number
    const numericValue = isNaN(currentValue) ? 0 : currentValue;

    // Apply increment if below max
    if (props.max === undefined || numericValue < props.max) {
      internalValue.value = numericValue + 1;
      updateValue();
    }
  }
};

const decrement = () => {
  if (internalValue.value === '') {
    return;
  } else {
    // Convert string to number if needed
    const currentValue = typeof internalValue.value === 'string' ? parseFloat(internalValue.value.replace(',', '.')) : internalValue.value;

    // Check if it's a valid number
    const numericValue = isNaN(currentValue) ? 0 : currentValue;

    // Apply decrement if above min
    if (props.min === undefined || numericValue > props.min) {
      internalValue.value = numericValue - 1;
      updateValue();
    }
  }
};
const propsClasses = computed(() => {
  return props.class ? props.class.split(' ').map((c) => `!${c}`) : [];
});
const inputClass = computed(() => [
  'flex text-base w-full m-0 py-1.5 px-3 text-surface-900 dark:text-surface-0 placeholder:text-surface-400 dark:placeholder:text-surface-500',
  'bg-surface-0 dark:bg-surface-900 outline-none focus:ring-primary-500 dark:focus:ring-primary-500 focus:outline-none focus:outline-offset-0 focus:ring-1 focus:ring-inset focus:ring-primary-600 dark:focus:ring-primary-500',
  props.showButtons ? 'rounded-md rounded-tr-none rounded-br-none rounded-tl-none rounded-bl-none order-2 border-x' : 'rounded-md border',
  props.invalid ? 'border-red-500 dark:border-red-500' : 'border-surface-300 dark:border-surface-700',
  props.disabled ? 'opacity-60 select-none pointer-events-none cursor-default' : '',
]);

const buttonClass = computed(() => [
  'flex items-center justify-center text-center align-bottom relative text-surface-600 dark:text-surface-400 bg-surface-0 dark:bg-surface-800',
  'w-[3rem] px-2.5 py-1.5 focus:outline-none focus:outline-offset-0 focus:ring-2 focus:ring-inset focus:ring-primary-600 dark:focus:ring-primary-500 hover:bg-surface-50 dark:hover:bg-surface-700 cursor-pointer select-none',
]);

const buttonDecrementClass = computed(() => [...buttonClass.value, 'rounded-md rounded-tr-none rounded-br-none order-1']);

const buttonIncrementClass = computed(() => [...buttonClass.value, 'rounded-md rounded-bl-none rounded-tl-none order-3']);

const inputModeValue = computed(() => {
  // On iOS devices, "decimal" enables the numeric keyboard with decimal point
  return props.mode === 'standard' ? 'numeric' : 'decimal';
});
</script>

<template>
  <template v-if="props.showButtons">
    <span
      class="inline-flex rounded-md shadow-sm ring-1 ring-surface-300 ring-offset-0 dark:ring-surface-700"
      :class="propsClasses"
      data-pc-name="inputnumber"
    >
      <button :class="buttonDecrementClass" type="button" aria-hidden="true" tabindex="-1" :disabled="disabled" @click="decrement">
        <span class="pi pi-minus"></span>
      </button>
      <input
        :id="inputId"
        v-model="internalValue"
        :type="minFractionDigits !== undefined ? 'text' : 'number'"
        :placeholder="placeholder"
        :min="min"
        :max="max"
        :class="[...inputClass, ...propsClasses]"
        :inputmode="inputModeValue"
        :step="step"
        :disabled="disabled"
        @input="updateValue"
        @blur="onBlur"
      />
      <button :class="buttonIncrementClass" type="button" aria-hidden="true" tabindex="-1" :disabled="disabled" @click="increment">
        <span class="pi pi-plus"></span>
      </button>
    </span>
  </template>

  <template v-else>
    <input
      :id="inputId"
      v-model="internalValue"
      :type="props.mode === 'decimal' || minFractionDigits !== undefined ? 'text' : 'number'"
      :step="step"
      :placeholder="placeholder"
      :min="min"
      :max="max"
      :class="[...inputClass, ...propsClasses]"
      :inputmode="inputModeValue"
      :disabled="disabled"
      @input="updateValue"
      @blur="onBlur"
    />
  </template>
</template>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
}
</style>
